import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { SEARCH_ONBOARDING_USER_SESSIONS, GET_USER_ONBOARDING_SESSION } from '../../../graphql/queries';

function OnboardingInspectorPage() {
  const [query, setQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const { data: searchData, loading: searching } = useQuery(SEARCH_ONBOARDING_USER_SESSIONS, {
    variables: { query, limit: 20 },
    skip: query.length < 2,
  });

  const { data: sessionData, loading: loadingSession } = useQuery(GET_USER_ONBOARDING_SESSION, {
    variables: { userId: selectedUserId },
    skip: !selectedUserId,
  });

  const session = sessionData?.onboardingSession;

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  return (
    <AdminLayout title="Onboarding Inspector" description="Search and inspect user onboarding sessions">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-3">Search Users</h3>
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter email or username..."
              className="w-full mb-3"
            />

            {query.length < 2 ? (
              <div className="text-sm text-gray-500">Type at least 2 characters to search</div>
            ) : searching ? (
              <div className="text-sm text-gray-500">Searching...</div>
            ) : searchData?.onboardingUserSessions?.length === 0 ? (
              <div className="text-sm text-gray-500">No results found</div>
            ) : (
              <div className="space-y-2">
                {searchData?.onboardingUserSessions?.map((result: any) => (
                  <button
                    key={result.id}
                    onClick={() => setSelectedUserId(result.user.asfId)}
                    className={`w-full text-left p-3 rounded-lg border ${
                      selectedUserId === result.user.asfId
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-medium">{result.user.email}</div>
                    <div className="text-xs text-gray-500">
                      {result.user.firstName} {result.user.lastName}
                    </div>
                    <div className="text-xs text-gray-500">
                      Status:{' '}
                      <span
                        className={
                          result.outcome === 'COMPLETED'
                            ? 'text-green-600'
                            : result.outcome === 'ABANDONED'
                              ? 'text-red-600'
                              : 'text-blue-600'
                        }
                      >
                        {result.outcome}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!selectedUserId ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              Select a user to view their onboarding session
            </div>
          ) : loadingSession ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading session...</div>
          ) : !session ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-red-500">Session not found</div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3">Session Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">User</div>
                    <div className="font-medium">
                      {session.user.firstName} {session.user.lastName}
                    </div>
                    <div className="text-gray-500 text-xs">{session.user.email}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Flow Version</div>
                    <div className="font-medium">
                      {session.flowVersion.name} (v{session.flowVersion.version})
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Started</div>
                    <div className="font-medium">{new Date(session.startedAt).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          session.outcome === 'COMPLETED'
                            ? 'bg-green-100 text-green-800'
                            : session.outcome === 'ABANDONED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {session.outcome}
                      </span>
                    </div>
                  </div>
                  {session.completedAt && (
                    <div>
                      <div className="text-gray-500">Completed</div>
                      <div className="font-medium">{new Date(session.completedAt).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-3">Event Timeline ({session.events.length} events)</h3>
                <div className="space-y-2">
                  {session.events.map((event: any) => {
                    const isExpanded = expandedEvents.has(event.id);
                    const isError = event.eventType === 'ERROR';

                    return (
                      <div
                        key={event.id}
                        className={`border rounded-lg ${isError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                      >
                        <button
                          onClick={() => toggleEventExpanded(event.id)}
                          className="w-full p-3 text-left flex items-start justify-between hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-mono text-sm ${isError ? 'text-red-700' : 'text-gray-900'}`}>
                                {event.stepCode}
                              </span>
                              <span
                                className={`px-2 py-0.5 text-xs rounded ${
                                  event.eventType === 'SUBMITTED'
                                    ? 'bg-green-100 text-green-700'
                                    : event.eventType === 'SKIPPED'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : event.eventType === 'ERROR'
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {event.eventType}
                              </span>
                              {event.durationMs && (
                                <span className="text-xs text-gray-500">{(event.durationMs / 1000).toFixed(1)}s</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{new Date(event.createdAt).toLocaleString()}</div>
                          </div>
                          <div className="ml-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3">
                            <div className="text-xs font-medium text-gray-500 mb-1">Payload:</div>
                            <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded overflow-auto max-h-48">
                              {JSON.stringify(event.payload, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default OnboardingInspectorPage;
